"use client";

import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { DropdownMenu } from "@package/subframe/components/DropdownMenu";
import { IconButton } from "@package/subframe/components/IconButton";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import { Table } from "@package/subframe/components/Table";
import { TextField } from "@package/subframe/components/TextField";
import {
  FeatherBuilding2,
  FeatherMoreHorizontal,
  FeatherPencil,
  FeatherPlus,
  FeatherSearch,
} from "@subframe/core";
import * as SubframeCore from "@subframe/core";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { Pagination } from "@/components/ui/pagination";
import { Spinner } from "@/components/ui/spinner";
import { TABLE_MIN_WIDTH } from "@/components/ui/table-scroll-container";
import { useAuth } from "@/contexts/AuthContext";
import type { OrganisationItemDto } from "@/generated/model";
import { useListOrganisationsQueryControllerList } from "@/generated/organisations/organisations";
import { DEFAULT_PAGE_SIZE } from "@/lib/pagination";

import { CreateOrganisationModal } from "../CreateOrganisationModal";

export function OrganisationList() {
  const { isPlatformAdmin } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [page, setPage] = useState(1); // 1-based, matches the Pagination component
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const {
    data: organisationsData,
    isLoading,
    isError,
  } = useListOrganisationsQueryControllerList(
    {},
    {
      query: {
        retry: false,
      },
    },
  );

  // Handle error state
  if (isError) {
    toast.error("Failed to load nursery groups");
  }

  const organisations = useMemo(
    () => (organisationsData?.data?.data || []) as OrganisationItemDto[],
    [organisationsData],
  );

  // Filter organisations by search query
  const filteredOrganisations = useMemo(() => {
    if (!searchQuery.trim()) return organisations;

    const query = searchQuery.toLowerCase();
    return organisations.filter(
      (org) =>
        org.name.toLowerCase().includes(query) ||
        org.postcode?.toLowerCase().includes(query) ||
        org.country?.toLowerCase().includes(query),
    );
  }, [organisations, searchQuery]);

  // The endpoint has no search parameter, so the filter runs over the whole set
  // and the page is sliced from the result rather than requested from the API.
  const pagedOrganisations = useMemo(
    () => filteredOrganisations.slice((page - 1) * pageSize, page * pageSize),
    [filteredOrganisations, page, pageSize],
  );

  return (
    <div className="flex w-full flex-col items-start gap-8 bg-white animate-fade-in">
      {/* Header Section */}
      <div className="flex w-full flex-col items-start gap-1">
        <div className="flex w-full flex-wrap items-center gap-4">
          <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
            <span className="w-full whitespace-nowrap text-metrics font-metrics text-primary-navy-800">
              Nursery Groups
            </span>
            <span className="text-body font-body text-subtext-color">
              Manage nursery groups on the platform
            </span>
          </div>
          {isPlatformAdmin && (
            <Button
              variant="brand-primary"
              icon={<FeatherPlus />}
              onClick={() => setIsCreateOpen(true)}
            >
              Create Nursery Group
            </Button>
          )}
        </div>
      </div>

      {/* Search Section */}
      <div className="flex w-full flex-wrap items-center gap-4">
        <TextField
          className="h-auto w-full max-w-xs"
          variant="filled"
          icon={<FeatherSearch />}
        >
          <TextField.Input
            placeholder="Search nursery groups..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
          />
        </TextField>
      </div>

      {/* Content Section */}
      <div className="flex w-full flex-col items-start gap-8">
        {isLoading ? (
          <div className="flex w-full items-center justify-center py-12">
            <Spinner className="h-8 w-8 text-primary" />
          </div>
        ) : filteredOrganisations.length === 0 ? (
          <div className="flex w-full flex-col items-center justify-center py-12 gap-4">
            <IconWithLogoBackground variant="neutral" size="x-large" />
            <div className="flex flex-col items-center gap-2">
              <span className="text-heading-2 font-heading-2 text-default-font">
                {searchQuery
                  ? "No nursery groups found"
                  : "No nursery groups yet"}
              </span>
              <span className="text-body font-body text-subtext-color text-center max-w-md">
                {searchQuery
                  ? "Try adjusting your search query."
                  : isPlatformAdmin
                    ? "Create your first nursery group to get started."
                    : "No nursery groups available."}
              </span>
            </div>
            {isPlatformAdmin && !searchQuery && (
              <div className="flex items-center gap-2 pt-4">
                <Button
                  variant="brand-primary"
                  icon={<FeatherPlus />}
                  onClick={() => setIsCreateOpen(true)}
                >
                  Create Nursery Group
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="flex w-full flex-col items-start bg-default-background overflow-auto">
            <Table
              className={TABLE_MIN_WIDTH.md}
              header={
                <Table.HeaderRow>
                  <Table.HeaderCell>Nursery group name</Table.HeaderCell>
                  <Table.HeaderCell>Location</Table.HeaderCell>
                  <Table.HeaderCell>Plan</Table.HeaderCell>
                  <Table.HeaderCell>Nurseries</Table.HeaderCell>
                  <Table.HeaderCell>Notes</Table.HeaderCell>
                  <Table.HeaderCell className="items-center justify-end">
                    Actions
                  </Table.HeaderCell>
                </Table.HeaderRow>
              }
            >
              {pagedOrganisations.map((org) => (
                <Table.Row key={org.slug}>
                  <Table.Cell>
                    <Link
                      to="/organisations/$slug"
                      params={{ slug: org.slug }}
                      className="flex items-center gap-3"
                    >
                      <Badge variant="neutral" icon={<FeatherBuilding2 />}>
                        {org.name}
                      </Badge>
                    </Link>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-body font-body text-subtext-color">
                      {org.postcode}, {org.country}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge variant="neutral">{org.plan ?? "LAUNCH"}</Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-body font-body text-subtext-color">
                      {org.nurseryCount ?? 0} / {org.maxNurseries}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-body font-body text-subtext-color line-clamp-1">
                      {org.notes || "—"}
                    </span>
                  </Table.Cell>
                  <Table.Cell variant="aligned-right">
                    <div className="flex items-center justify-end gap-2">
                      <SubframeCore.DropdownMenu.Root>
                        <SubframeCore.DropdownMenu.Trigger asChild={true}>
                          <IconButton
                            variant="neutral-primary"
                            size="medium"
                            icon={<FeatherMoreHorizontal />}
                          />
                        </SubframeCore.DropdownMenu.Trigger>
                        <SubframeCore.DropdownMenu.Portal>
                          <SubframeCore.DropdownMenu.Content
                            side="bottom"
                            align="end"
                            sideOffset={4}
                            asChild={true}
                          >
                            <DropdownMenu>
                              <Link
                                to="/organisations/$slug"
                                params={{ slug: org.slug }}
                                className="w-full"
                              >
                                <DropdownMenu.DropdownItem
                                  icon={<FeatherPencil />}
                                >
                                  View / Edit
                                </DropdownMenu.DropdownItem>
                              </Link>
                            </DropdownMenu>
                          </SubframeCore.DropdownMenu.Content>
                        </SubframeCore.DropdownMenu.Portal>
                      </SubframeCore.DropdownMenu.Root>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table>

            <Pagination
              className="pt-4"
              page={page}
              pageSize={pageSize}
              totalCount={filteredOrganisations.length}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
              itemNoun="nursery groups"
            />
          </div>
        )}
      </div>

      {/* Create Nursery Group Modal */}
      <CreateOrganisationModal
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
      />
    </div>
  );
}
